/**
 * TripCard — single trip tile on the Dashboard. Extracted from
 * `Dashboard.js` (Feb 2026) and wrapped in `React.memo` so the ~20-card
 * trip list doesn't re-render the entire tree on every Dashboard state
 * change (dialog open, hover, search input, etc.).
 *
 * Memoization equality: shallow on `trip` (the parent re-uses the same
 * reference unless `setTrips` is called), `currentUser?.id`, and the
 * view-only flag. Click/edit/delete handlers come from the parent and
 * are expected to be stable refs (parent uses `useCallback`).
 */
import { memo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  Users,
  Edit,
  Trash2,
  Moon,
  ListChecks,
  MapPin,
} from "lucide-react";
import { getCountryFlag } from "@/utils/countryFlags";

const getInitials = (name) => {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase();
  const firstWord = words[0];
  const lastWord = words[words.length - 1];
  const firstInitial = firstWord.split('-')[0][0];
  const lastInitial = lastWord[0];
  return (firstInitial + lastInitial).toUpperCase();
};

const TripCard = ({
  trip,
  formatDate,
  currentUserId,
  isViewOnly,
  onOpen,
  onEdit,
  onDelete,
  onPrefetch,
}) => {
  const isOwner = trip.created_by_user_id === currentUserId;
  const showActions = !isViewOnly && isOwner;

  return (
    <Card
      data-testid={`trip-card-${trip.id}`}
      className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm hover:shadow-xl cursor-pointer border border-gray-200 dark:border-gray-700 active:scale-[0.98]"
      style={{ transition: 'transform 0.2s, box-shadow 0.2s' }}
      onClick={() => onOpen(trip.id)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        // Warm the trip detail cache while the user is reading the card.
        // By the time they click, the full-context payload is in memory
        // and TripDetails renders without a spinner.
        onPrefetch?.(trip.id);
      }}
      onFocus={() => onPrefetch?.(trip.id)}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg sm:text-xl text-gray-900 dark:text-gray-100 leading-tight">{trip.name}</CardTitle>
            <CardDescription className="mt-2">
              <div className="flex items-center text-gray-700 dark:text-gray-300">
                <Calendar className="mr-2 h-4 w-4 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="text-sm font-medium">
                  {formatDate(trip.start_date)} - {formatDate(trip.end_date)}
                </span>
              </div>

              {trip.designatedTravelers && trip.designatedTravelers.length > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <Users className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                  <span className="text-xs text-gray-600">Trip Travelers:</span>
                  <div className="flex -space-x-2">
                    {trip.designatedTravelers.map(traveler => {
                      const initials = getInitials(traveler.name);
                      return (
                        <div
                          key={traveler.id}
                          className="relative group"
                          title={traveler.name}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {traveler.image_url ? (
                            <img
                              src={traveler.image_url}
                              alt={traveler.name}
                              className="w-7 h-7 rounded-full border-2 border-white shadow-sm hover:scale-110 transition-transform"
                            />
                          ) : (
                            <div className="w-7 h-7 rounded-full border-2 border-white shadow-sm bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white text-[10px] font-bold hover:scale-110 transition-transform">
                              {initials}
                            </div>
                          )}
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                            {traveler.name}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-2">
            {(!isOwner || trip.hasShares) && (
              <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 flex items-center gap-1">
                <Users className="h-3 w-3" />
                Shared
              </Badge>
            )}
            {showActions && (
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-sky-500 hover:text-sky-700 hover:bg-sky-50 min-w-[40px] min-h-[40px] sm:min-w-[36px] sm:min-h-[36px]"
                  onClick={(e) => onEdit(e, trip)}
                  title="Edit Trip"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 min-w-[40px] min-h-[40px] sm:min-w-[36px] sm:min-h-[36px]"
                  onClick={(e) => onDelete(e, trip)}
                  title="Delete Trip"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {trip.description && (
          <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed mb-3">{trip.description}</p>
        )}

        {trip.stats && (
          <div className="space-y-3 mt-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 px-2.5 py-1 rounded-full border border-purple-200 dark:border-purple-800">
                <Moon className="h-3.5 w-3.5" />
                <span className="text-xs font-semibold">{trip.stats.nights}</span>
                <span className="text-xs">Nights</span>
              </div>
              <div className="flex items-center gap-1.5 bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300 px-2.5 py-1 rounded-full border border-green-200 dark:border-green-800">
                <ListChecks className="h-3.5 w-3.5" />
                <span className="text-xs font-semibold">{trip.stats.completedNights}</span>
                <span className="text-xs">Completed</span>
              </div>
              <div className="flex items-center gap-1.5 bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 px-2.5 py-1 rounded-full border border-sky-200 dark:border-sky-800">
                <MapPin className="h-3.5 w-3.5" />
                <span className="text-xs font-semibold">{trip.stats.countries}</span>
                <span className="text-xs">Countries</span>
              </div>
            </div>

            {trip.stats.countryNames && trip.stats.countryNames.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap" data-testid={`trip-country-flags-${trip.id}`}>
                {trip.stats.countryNames.map((name) => {
                  const flag = getCountryFlag(name);
                  return flag ? (
                    <span key={name} className="text-base leading-none" title={name}>{flag}</span>
                  ) : null;
                })}
              </div>
            )}

            {trip.stats.totalNights > 0 && (
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 dark:from-gray-800 dark:to-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-1">
                  <Moon className="h-3 w-3" />
                  Accommodation Status:
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {trip.stats.pendingNights > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                      <span className="text-xs text-gray-700">
                        <span className="font-bold text-red-700">{trip.stats.pendingNights}</span> pending
                      </span>
                    </div>
                  )}
                  {trip.stats.completedNights > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-xs text-gray-700">
                        <span className="font-bold text-green-700">{trip.stats.completedNights}</span> completed
                      </span>
                    </div>
                  )}
                  {trip.stats.bookedNights > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <span className="text-xs text-gray-700">
                        <span className="font-bold text-blue-700">{trip.stats.bookedNights}</span> booked
                      </span>
                    </div>
                  )}
                  {trip.stats.plannedNights > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                      <span className="text-xs text-gray-700">
                        <span className="font-bold text-orange-700">{trip.stats.plannedNights}</span> planned
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-[10px] text-gray-500 mt-1">
                  {trip.stats.totalNights} total night{trip.stats.totalNights !== 1 ? 's' : ''} •
                  {trip.stats.pendingNights > 0 ? (
                    <span className="text-red-600 font-semibold ml-1">⚠️ Action needed</span>
                  ) : (
                    <span className="text-green-600 font-semibold ml-1">✓ All accounted</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Custom equality — re-render only when the trip object reference or
// the user/permission inputs actually change. The handler/formatDate
// refs are intentionally NOT compared (callers must provide stable
// refs via useCallback / module-level).
const areEqual = (prev, next) =>
  prev.trip === next.trip &&
  prev.currentUserId === next.currentUserId &&
  prev.isViewOnly === next.isViewOnly;

export default memo(TripCard, areEqual);
